'use client';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { AiOutlineHeart } from 'react-icons/ai';

export type HeaderFavoritesButtonProps = {
  className?: {
    root?: string;
    info?: string;
    title?: string;
    price?: string;
    badge?: string;
  };
};

export function HeaderFavoritesButton({ className }: HeaderFavoritesButtonProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const openDialog = useDialogStore(s => s.open);

  const me = useCustomerStore(s => s.me);

  return (
    <Button
      asChild
      size="custom"
      className="h-6 w-6 rounded-none bg-transparent shadow-none transition-all hover:bg-transparent hover:opacity-70"
    >
      <Link
        href={ROUTES.FAVORITES()}
        aria-label={locale === 'sk' ? 'Obľúbené produkty' : 'Favorite products'}
        onClick={e => {
          if (me?.customer) return;
          e.preventDefault();
          e.stopPropagation();
          openDialog({
            id: SignInDialog.id,
            content: <SignInDialog />,
          });
        }}
        prefetch={false}
      >
        <div className="relative">
          <FavoritesBadge />
          <AiOutlineHeart className="h-6 w-6" color="black" />
        </div>
      </Link>
    </Button>
  );
}

export function FavoritesBadge() {
  const { me, isInitialized } = useCustomerStore();
  const locale = useLocaleStore(s => s.currentLocale);

  const favoriteItems = useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, locale, me?.authToken ?? null],
    queryFn: () =>
      // TODO: add query that fetches only count
      fetchProducts({
        locale,
        authToken: me ? me!.authToken : '',
        query: { favorite: true, results: 9999 },
      }),
    enabled: isInitialized && !!me?.authToken,
  });

  const favoriteItemsCount = favoriteItems.data?.data?.length ?? 0;

  const countText =
    favoriteItemsCount > 99 ? (
      <span>
        99<sup>+</sup>
      </span>
    ) : (
      favoriteItemsCount
    );

  return (
    <Badge
      className={cn(
        'absolute right-0 top-0 h-5 w-5 -translate-y-1/2 translate-x-1/2 justify-center bg-destructive-foreground text-[10px] text-white',
        'ea-favorites-button__badge',
      )}
      size="circle"
    >
      {countText}
    </Badge>
  );
}
